"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import "./SocialStyling.css"

const SearchInterface = () => {
  const navigate = useNavigate()
  const { userType } = useParams()
  const [searchQuery, setSearchQuery] = useState("")
  const [recentSearches, setRecentSearches] = useState([
    "jetpack compose",
    "games", 
    "UI",
    "backstate"
  ])
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = (query) => {
    if (query.trim()) {
      setIsSearching(true)
      // Add to recent searches if not already present
      if (!recentSearches.includes(query.trim())) {
        setRecentSearches(prev => [query.trim(), ...prev.slice(0, 4)])
      }
      
      // Simulate search - replace with actual search API
      setTimeout(() => {
        setIsSearching(false)
        // Navigate to search results or handle results here
      }, 1000)
    }
  }

  const handleRecentSearchClick = (search) => {
    setSearchQuery(search)
    handleSearch(search)
  }

  const removeRecentSearch = (searchToRemove) => {
    setRecentSearches(prev => prev.filter(search => search !== searchToRemove))
  }

  const clearAllRecentSearches = () => {
    setRecentSearches([])
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery)
    }
  }

  const handleBackClick = () => {
    navigate(-1) // Go back to previous page
  }

  return (
    <div className="searchinterface-container">
      {/* Search Header */}
      <div className="searchinterface-header">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="searchinterface-back-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#374151">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </button>

        {/* Search Input */}
        <div className="searchinterface-search-container">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search for posts, users, or pets..."
            className="searchinterface-search-input"
            autoFocus
          />
          
          {/* Search Icon */}
          <div className="searchinterface-search-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#9CA3AF">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </div>

          {/* Search Button */}
          {searchQuery && (
            <button
              onClick={() => handleSearch(searchQuery)}
              disabled={isSearching}
              className="searchinterface-search-btn"
            >
              {isSearching ? (
                <div className="searchinterface-search-spinner" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFFFFF">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Search Content */}
      <div className="searchinterface-content">
        {/* Recent Searches */}
        {recentSearches.length > 0 && !searchQuery && (
          <div className="searchinterface-section">
            {/* Header */}
            <div className="searchinterface-recent-header">
              <h3 className="searchinterface-section-title">
                Recent searches
              </h3>
              
              <button
                onClick={clearAllRecentSearches}
                className="searchinterface-clear-btn"
              >
                ×
              </button>
            </div>

            {/* Recent Search Items */}
            <div className="searchinterface-recent-list">
              {recentSearches.map((search, index) => (
                <div 
                  key={index}
                  className="searchinterface-recent-item"
                  onClick={() => handleRecentSearchClick(search)}
                >
                  <span className="searchinterface-recent-text">
                    {search}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeRecentSearch(search)
                    }}
                    className="searchinterface-remove-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Suggestions - shown when typing */}
        {searchQuery && !isSearching && (
          <div className="searchinterface-section">
            <h3 className="searchinterface-suggestions-title">
              Search suggestions
            </h3>
            
            <div className="searchinterface-suggestions-list">
              {/* Mock suggestions based on search query */}
              {[
                `${searchQuery} users`,
                `${searchQuery} posts`,
                `${searchQuery} pets`,
                `${searchQuery} veterinarians`
              ].map((suggestion, index) => (
                <div 
                  key={index}
                  onClick={() => handleSearch(suggestion)}
                  className="searchinterface-suggestion-item"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#9CA3AF">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                  <span className="searchinterface-suggestion-text">
                    {suggestion}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isSearching && (
          <div className="searchinterface-loading-container">
            <div className="searchinterface-loading-spinner" />
            <p className="searchinterface-loading-text">
              Searching for "{searchQuery}"...
            </p>
          </div>
        )}

        {/* Empty State - when no recent searches */}
        {recentSearches.length === 0 && !searchQuery && !isSearching && (
          <div className="searchinterface-empty-container">
            <div className="searchinterface-empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#9CA3AF">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </div>
            
            <h3 className="searchinterface-empty-title">
              Start searching
            </h3>
            
            <p className="searchinterface-empty-subtitle">
              Search for posts, users, pets, or veterinarians in the Sulhafa community
            </p>
          </div>
        )}

        {/* Quick Search Categories */}
        {!searchQuery && !isSearching && (
          <div className="searchinterface-categories-container">
            <h3 className="searchinterface-section-title">
              Quick search
            </h3>
            
            <div className="searchinterface-categories-grid">
              {[
                { name: 'Popular posts', icon: '📝', query: 'popular posts' },
                { name: 'Veterinarians', icon: '👨‍⚕️', query: 'veterinarians' },
                { name: 'Pet care tips', icon: '💡', query: 'pet care tips' },
                { name: 'Dog training', icon: '🐕', query: 'dog training' },
                { name: 'Cat health', icon: '🐱', query: 'cat health' },
                { name: 'Pet nutrition', icon: '🥘', query: 'pet nutrition' }
              ].map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleSearch(category.query)}
                  className="searchinterface-category-btn"
                >
                  <span className="searchinterface-category-icon">{category.icon}</span>
                  <span className="searchinterface-category-text">
                    {category.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default SearchInterface